'use client';

import React, { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { logger } from '@/lib/logger';

interface DebugPanelContextType {
  isOpen: boolean;
  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
}

const DebugPanelContext = createContext<DebugPanelContextType | undefined>(undefined);

/**
 * Provider for debug panel state management
 * Manages open/close state and persists to sessionStorage for the current session
 * 
 * React 19 Best Practices:
 * - Uses functional state updates for togglePanel to ensure correct state transitions
 * - useCallback for context value functions to prevent unnecessary re-renders of consumers
 * - Proper cleanup in useEffect for sessionStorage synchronization
 */
export function DebugPanelProvider({ children }: { children: ReactNode }) {
  // Initialize state from sessionStorage if available (for same session persistence)
  // Otherwise start closed
  const [isOpen, setIsOpen] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    const stored = sessionStorage.getItem('debug-panel-open');
    return stored === 'true';
  });
  
  // Sync sessionStorage on state changes (for same session only)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('debug-panel-open', String(isOpen));
      // State synced (details in app logs)
    }
  }, [isOpen]);

  // Open panel - use functional update to ensure correct state transition
  const openPanel = useCallback(() => {
    logger.info('Debug panel opened');
    setIsOpen(true);
  }, []);

  // Close panel - use functional update to ensure correct state transition
  const closePanel = useCallback(() => {
    logger.info('Debug panel closed');
    setIsOpen(false);
  }, []);

  // Toggle panel - use functional state update (React 19 best practice)
  const togglePanel = useCallback(() => {
    // Toggle action (details in app logs)
    setIsOpen((prev) => !prev);
  }, []);

  // Memoize context value to prevent unnecessary re-renders of consumers
  // React 19: Still use useCallback/useMemo for context values to prevent consumer re-renders
  const contextValue = React.useMemo(
    () => ({ isOpen, openPanel, closePanel, togglePanel }),
    [isOpen, openPanel, closePanel, togglePanel]
  );

  return (
    <DebugPanelContext.Provider value={contextValue}>
      {children}
    </DebugPanelContext.Provider>
  );
}

export function useDebugPanel() {
  const context = useContext(DebugPanelContext);
  if (context === undefined) {
    throw new Error('useDebugPanel must be used within a DebugPanelProvider');
  }
  return context;
}

